import { useState, useCallback } from 'react';

interface UseTemplateModalProps<T> {
  onSubmit: (data: T) => Promise<void>;
  loading?: boolean;
}

export const useTemplateModal = <T extends Record<string, string>>({
  onSubmit,
}: UseTemplateModalProps<T>) => {
  const [formData, setFormData] = useState<T>({} as T);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent, requiredFields: string[]) => {
    e.preventDefault();

    const newErrors: Record<string, string> = {};
    requiredFields.forEach(field => {
      if (!formData[field]?.toString().trim()) {
        newErrors[field] = `${field.charAt(0).toUpperCase() + field.slice(1)} is required`;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error) {
      // Error is handled by the parent component
    }
  };

  const resetForm = useCallback((initialData: T) => {
    setFormData(initialData);
    setErrors({});
  }, []);

  return {
    formData,
    setFormData,
    errors,
    setErrors,
    handleInputChange,
    handleSubmit,
    resetForm,
  };
};
